/**
 * GasWishlist — client-only "save for later" heart toggle. Shopify has no
 * native wishlist object; this is local-storage sugar only, not synced to
 * the customer account. Items are keyed by product handle (not id) so the
 * wishlist page can re-fetch each product via /products/{handle}.js, the
 * same public JSON endpoint assets/recently-viewed.js relies on.
 */
(function () {
  const STORAGE_KEY = 'gasseth:wishlist';

  function readIds() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  function has(id) {
    return readIds().includes(String(id));
  }

  function toggle(id) {
    const key = String(id);
    const ids = readIds();
    const index = ids.indexOf(key);
    if (index === -1) {
      ids.push(key);
    } else {
      ids.splice(index, 1);
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { ids } }));
    return index === -1;
  }

  function bindButtons(root = document) {
    root.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const id = btn.dataset.wishlistToggle;
      btn.setAttribute('aria-pressed', String(has(id)));
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const saved = toggle(id);
        btn.setAttribute('aria-pressed', String(saved));
      });
    });
  }

  function cardHtml(product) {
    const image = product.featured_image ? product.featured_image.replace(/(\.[^.?]+)(\?|$)/, '_400x$1$2') : '';
    const onSale = !!(product.compare_at_price && product.compare_at_price > product.price);
    const price = window.GasCart ? GasCart.formatMoney(product.price) : `$${(product.price / 100).toFixed(2)}`;
    const compareAt = onSale
      ? `<span class="product-price__old">${window.GasCart ? GasCart.formatMoney(product.compare_at_price) : ''}</span>`
      : '';

    // Mirrors snippets/product-card.liquid's markup (stretched link,
    // media wrapper, body wrapper) so this client-rendered card can reuse
    // that snippet's CSS, which is duplicated in this section's
    // {% stylesheet %} since the snippet itself is never Liquid-rendered
    // on this page.
    return `
    <div class="product-card" data-wishlist-item="${product.handle}">
      <a class="product-card__link" href="${product.url}" aria-label="Ver producto: ${product.title}"></a>
      <div class="product-card__media">
        <img class="product-card__image" src="${image}" alt="${product.title}" loading="lazy" width="280" height="280" />
        <button type="button" class="product-card__icon-btn product-card__wishlist" aria-label="Quitar de la lista de deseos" data-wishlist-toggle="${product.handle}" aria-pressed="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z"/></svg>
        </button>
      </div>
      <div class="product-card__body">
        <p class="product-card__name">${product.title}</p>
        <div class="product-card__price">
          <span class="product-price__current ${onSale ? 'product-price__current--sale' : ''}">${price}</span>
          ${compareAt}
        </div>
      </div>
    </div>`;
  }

  async function render(container, { emptyMessage = '' } = {}) {
    const handles = readIds();
    const section = container.closest('[data-wishlist-section]');

    if (handles.length === 0) {
      if (section) section.classList.add('is-empty');
      if (emptyMessage) container.innerHTML = '';
      return;
    }

    const products = await Promise.all(
      handles.map((handle) =>
        fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null),
      ),
    );

    const valid = products.filter(Boolean);
    if (valid.length === 0) {
      if (section) section.classList.add('is-empty');
      return;
    }

    container.innerHTML = valid.map(cardHtml).join('');
    if (section) section.classList.remove('is-empty');

    container.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      event.preventDefault();
      event.stopPropagation();
      toggle(btn.dataset.wishlistToggle);
      const card = btn.closest('[data-wishlist-item]');
      if (card) card.remove();
      if (!container.children.length && section) section.classList.add('is-empty');
    });
  }

  window.GasWishlist = { has, toggle, bindButtons, render };
  document.addEventListener('DOMContentLoaded', () => bindButtons());
})();
